import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const BLOG_POSTS = [
	{
		id: 1,
		title: 'Why Your Data Room Should Be More Than a Shared Folder',
		date: 'March 10, 2025',
		excerpt: 'Investors form opinions in the first few minutes. Here’s how a proper data room helps you make the right impression and keep sensitive information under control.',
		content: (
			<>
				<p className="text-gray-700 mb-4">
					When you send a Google Drive or Dropbox link to an investor, you’re not just sharing files—you’re handing over control. You can’t see who opened what, when they did it, or whether they’ve shared the link. Revoking access means chasing them or changing the link for everyone.
				</p>
				<p className="text-gray-700 mb-4">
					A dedicated data room changes that. You assign access by person or group, see exactly who viewed which document and for how long, and revoke access in seconds. You can gate the room behind an NDA so no one sees a single file until they’ve signed. For investors, it signals that you take security and professionalism seriously—which matters when they’re comparing you to other deals.
				</p>
				<p className="text-gray-700 mb-4">
					The best part: setting up a proper data room doesn’t have to take days. With the right tool, you can upload your deck and key documents, set permissions, and send a single secure link. Your raise deserves more than a shared folder.
				</p>
			</>
		),
	},
	{
		id: 2,
		title: '5 Things Investors Look for in a Startup Data Room',
		date: 'February 28, 2025',
		excerpt: 'What makes a data room “investor-ready”? We asked fund managers and angels what they notice first—and what makes them close the tab.',
		content: (
			<>
				<p className="text-gray-700 mb-4">
					<strong>1. Clear structure.</strong> A flat dump of 50 files with vague names (“Doc1.pdf”, “Financials_final_v2”) is a red flag. Organize by category: pitch deck, financials, legal, cap table, product. Use consistent naming so investors can find what they need without asking.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>2. Up-to-date information.</strong> Stale numbers or old decks suggest the room was set up once and forgotten. Update key documents when you have new metrics or a new round. Investors notice when the “last modified” date is recent.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>3. The right level of detail.</strong> Don’t hide everything; don’t dump everything. Share enough for serious evaluation—deck, key financials, cap table, key contracts—without overwhelming. You can always open more as diligence progresses.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>4. NDA and access control.</strong> Requiring an NDA before access shows you take confidentiality seriously. Granular permissions (e.g. different folders for different investors) show you understand how deal flow works.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>5. A single, professional link.</strong> One URL, one login, one place to ask questions. No “check the email from last week” or “the updated deck is in this other folder.” A clean, branded experience reflects well on your company.
				</p>
			</>
		),
	},
	{
		id: 3,
		title: 'How to Run a Pitch Event That Actually Closes Deals',
		date: 'February 15, 2025',
		excerpt: 'Pitch events can be noisy. Here’s how to structure yours so startups get real exposure and investors get deal flow they can act on.',
		content: (
			<>
				<p className="text-gray-700 mb-4">
					Pitch events work when three things align: the right startups, the right investors, and a clear way to continue the conversation after the event. Too often, events end with a room full of business cards and no clear next step.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>Curate, don’t pack.</strong> A small number of well-matched startups and investors beats a large room of random attendees. Screen startups for stage and sector; invite investors who actually write checks in that space. Quality of fit matters more than headcount.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>Give investors one place to go.</strong> After the pitch, send investors a single link where they can access decks, one-pagers, and data room links for every startup that presented. No chasing founders for materials—everything in one deal room or event hub. That’s where tools like iVault help: one platform for the event and the follow-up.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>Define the next step.</strong> Be explicit: “If you’re interested, request access to the data room by [date]” or “Founders will send NDA links to interested investors within 48 hours.” Clear expectations lead to real follow-through and more closed deals.
				</p>
			</>
		),
	},
	{
		id: 4,
		title: 'The Real Pain of Collecting Files from Startups—And Why We Built iVault',
		date: 'March 14, 2025',
		excerpt: 'Chasing decks, chasing versions, chasing signatures. Here are the biggest pain points we saw when collecting documents from startups—and how we solved them.',
		content: (
			<>
				<p className="text-gray-700 mb-4">
					<strong>The scatter problem.</strong> Startups send pitch decks over email, financials in a separate Google Sheet, cap tables in a different link, and legal docs "by next week." Investors and accelerators end up with a mess: 20 threads, 15 links, and no single place that's "the" data room. Finding the latest version of anything becomes a treasure hunt.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>Version chaos.</strong> "Please use the deck from the last email." "Actually, the one in the Drive is newer." "We just sent a new one." Multiple versions with no clear "final" create confusion and wasted time. No one wants to present on an outdated deck or make a decision on stale numbers.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>Access and security gaps.</strong> Links get forwarded, shared in Slack, or left in open spreadsheets. There's no way to know who saw what, and revoking access means asking everyone to "please delete that link." For sensitive data, that's a real risk.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>How we resolved it.</strong> We built iVault so that startups upload once into a single, structured data room. One link for investors, one place for you to see who accessed what, and one source of truth. No more scattered files, no more version roulette—just one professional room that stays up to date and under your control.
				</p>
			</>
		),
	},
	{
		id: 5,
		title: 'From "Send It When You Can" to "It\'s Already There": Fixing Startup Document Collection',
		date: 'March 12, 2025',
		excerpt: 'Why collecting documents from startups used to be a bottleneck—and how a structured data room turns it into a one-time, pain-free process.',
		content: (
			<>
				<p className="text-gray-700 mb-4">
					<strong>Pain point 1: Endless follow-ups.</strong> Program managers and investors spend hours chasing founders: "We still need your cap table." "Can you re-send the deck?" "Where's the financial model?" Startups are busy; emails get buried. Deadlines slip, and the quality of the "collection" is inconsistent—some send everything, some send fragments.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>Pain point 2: No standard structure.</strong> Every startup organizes files differently. One sends a ZIP, another a Drive link, another a Notion page. There's no common checklist, so you can't easily compare companies or ensure nothing is missing. Review becomes slow and error-prone.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>Pain point 3: Compliance and audit.</strong> When files live in email and random links, proving who had access to what—and when—is nearly impossible. For accelerators, funds, or partners with compliance needs, that's a real gap.
				</p>
				<p className="text-gray-700 mb-4">
					<strong>Our solution.</strong> With iVault, startups get a clear, guided flow: one place to upload their deck, financials, cap table, and other materials. You define what you need (e.g. "Pitch deck, last 12 months financials, cap table"); they upload once. You get a single link per startup, a consistent structure, and full visibility into who viewed what. No more chasing—everything is already there, in one place, the way you need it.
				</p>
			</>
		),
	},
];

const Blog = () => {
	return (
		<div className="min-h-screen bg-gray-50">
			<header className="bg-white shadow-sm border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex items-center justify-between">
						<Link to="/">
							<Logo showText={true} width={50} height={50} />
						</Link>
						<Link
							to="/"
							className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
						>
							Back to Home
						</Link>
					</div>
				</div>
			</header>

			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
					<h1 className="text-4xl font-bold text-gray-900 mb-4">Blog</h1>
					<p className="text-gray-600 mb-10 text-lg">
						Insights on fundraising, investor relations, data rooms, and secure document sharing. Tips for founders and investors, straight from the iVault team.
					</p>

					<div className="space-y-12">
						{BLOG_POSTS.map((post) => (
							<article key={post.id} className="border-b border-gray-200 pb-12 last:border-0 last:pb-0">
								<h2 className="text-2xl font-semibold text-gray-900 mb-2">{post.title}</h2>
								<p className="text-sm text-gray-500 mb-4">{post.date}</p>
								<p className="text-gray-600 mb-6 leading-relaxed">{post.excerpt}</p>
								<div className="prose prose-lg max-w-none text-gray-700">
									{post.content}
								</div>
							</article>
						))}
					</div>

					<p className="mt-10 text-gray-600 text-sm">
						More articles on pitch decks, investor outreach, and data room best practices coming soon. Have a topic you’d like us to cover? <Link to="/contact" className="text-blue-600 hover:underline">Get in touch</Link>.
					</p>
				</div>
			</div>
		</div>
	);
};

export default Blog;
